import React, { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  Calendar,
  Clock,
  RefreshCw,
  Loader2
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001';

const SLOT_TIMES = [
  "10:00 AM", "10:30 AM",
  "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM",
  "01:00 PM", "01:30 PM",
  "02:00 PM", "02:30 PM",
  "03:00 PM", "03:30 PM",
  "04:00 PM", "04:30 PM",
  "05:00 PM", "05:30 PM",
];

const todayISO = () => new Date().toISOString().split("T")[0];

const AdminSlots = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(todayISO());
  const [adminSlots, setAdminSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(null); // track which slot is toggling

  /* ================= FETCH ADMIN SLOTS ================= */
  const fetchSlots = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/slots`);
      const json = await res.json();
      setAdminSlots(json.adminSlots || []);
      setBookedSlots(json.bookedSlots || []);
    } catch (err) {
      console.error("❌ Fetch failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  /* ================= MERGE DEFAULT + DB ================= */
  const slotsForDate = useMemo(() => {
    // Helper to normalize DB time (e.g. "14:00:00" or "14:00 - 14:30") to UI format ("02:00 PM")
    const normalizeTime = (t) => {
      if (!t) return "";
      let start = t.toString().split(" - ")[0]; // Handle range
      start = start.split(":").slice(0, 2).join(":"); // Handle HH:MM:SS -> HH:MM

      const [h, m] = start.split(":").map(Number);
      if (isNaN(h) || isNaN(m)) return t; // Fallback

      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 || 12;
      return `${String(h12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
    };

    // 1. Map Admin Overrides (Block/Unblock)
    const adminMap = {};
    adminSlots
      .filter((s) => s.date === date)
      .forEach((s) => {
        const uiTime = normalizeTime(s.time);
        adminMap[uiTime] = s.is_open;
      });

    // 2. Map Confirmed Bookings
    const bookedMap = new Map();
    bookedSlots
      .forEach((b) => {
        // Handle date string format
        const bDate = new Date(b.date).toISOString().split('T')[0];
        if (bDate === date) {
          const uiTime = normalizeTime(b.time);
          bookedMap.set(uiTime, b);
        }
      });

    return SLOT_TIMES.map((time) => {
      const booking = bookedMap.get(time);
      const isBooked = !!booking;
      // If booked, is_open implies it's taken (not available for blocking/unblocking manually in the same sense)
      // We'll prioritize booking status in UI.
      // Admin override exists?
      const adminOverride = adminMap[time];

      return {
        time,
        is_open: adminOverride !== undefined ? adminOverride : true,
        is_booked: isBooked,
        bookingDetails: booking
      };
    });
  }, [date, adminSlots, bookedSlots]);

  /* ================= TOGGLE SLOT ================= */
  const toggleSlot = async (time, isOpen) => {
    setToggling(time);
    try {
      await fetch(`${API_BASE}/api/admin/slots/toggle`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          date,
          time,
          is_open: !isOpen,
        }),
      });
      fetchSlots();
    } catch (err) {
      console.error("❌ Toggle failed", err);
    } finally {
      setToggling(null);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden min-h-[600px] flex flex-col">
      {/* HEADER PANEL */}
      <div className="p-6 border-b border-gray-100 flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4 bg-gray-50/50">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/admin")}
            className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-500 hover:text-exiser-primary transition-colors"
            title="Back to Dashboard"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div>
            <h3 className="text-xl font-bold text-gray-900 uppercase tracking-tight">
              Slot Management
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              Manage availability for 1:1 Sessions.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full xl:w-auto">
          {/* DATE PICKER */}
          <div className="relative flex-1 xl:flex-none">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="pl-10 pr-4 py-2.5 rounded-lg border border-gray-200 focus:outline-none focus:border-exiser-primary focus:ring-1 focus:ring-exiser-primary text-sm bg-white shadow-sm w-full"
            />
          </div>

          <div className="h-8 w-px bg-gray-300 mx-1 hidden xl:block"></div>

          <button
            onClick={fetchSlots}
            className="p-2.5 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-exiser-primary transition-colors text-gray-500 shadow-sm"
            title="Refresh Data"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* CONTENT AREA */}
      <div className="p-8 bg-gray-50/30 flex-1">

        {loading && adminSlots.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Loader2 className="w-10 h-10 animate-spin mb-4 text-exiser-primary" />
            <p>Loading availability...</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {slotsForDate.map((slot) => {
              const isActive = slot.is_open;
              const isProcessing = toggling === slot.time;

              return (
                <div
                  key={slot.time}
                  className={`
                        relative group flex flex-col items-center justify-center p-6 rounded-xl border transition-all duration-200
                        ${slot.is_booked
                      ? "bg-purple-50 border-purple-200 shadow-sm"
                      : isActive
                        ? "bg-white border-gray-200 hover:border-green-300 hover:shadow-md"
                        : "bg-gray-50 border-gray-200 opacity-75 hover:opacity-100"
                    }
                    `}
                >
                  {/* Time Label */}
                  <div className="flex items-center gap-2 mb-3">
                    <Clock className={`w-4 h-4 ${isActive ? 'text-gray-400' : 'text-gray-300'}`} />
                    <span className={`font-bold text-lg ${isActive ? 'text-gray-800' : 'text-gray-400'}`}>
                      {slot.time}
                    </span>
                  </div>

                  {/* Status Badge */}
                  <div className={`
                            px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide mb-4 flex items-center gap-1.5
                            ${slot.is_booked
                      ? "bg-purple-50 text-exiser-primary border border-purple-100"
                      : isActive
                        ? "bg-green-50 text-green-700 border border-green-100"
                        : "bg-red-50 text-red-600 border border-red-100"
                    }
                        `}>
                    {slot.is_booked ? (
                      <div className="flex flex-col items-center">
                        <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-exiser-primary"></span> Booked</span>
                      </div>
                    ) : isActive ? (
                      <><span className="w-1.5 h-1.5 rounded-full bg-green-500"></span> Available</>
                    ) : (
                      <><span className="w-1.5 h-1.5 rounded-full bg-red-500"></span> Blocked by Admin</>
                    )}
                  </div>

                  {/* Booking Details (If Booked) */}
                  {slot.is_booked && slot.bookingDetails && (
                    <div className="mb-3 text-center">
                      <p className="text-xs font-bold text-gray-800 truncate max-w-[120px]" title={slot.bookingDetails.name}>
                        {slot.bookingDetails.name}
                      </p>
                      <p className="text-[10px] text-gray-500 truncate max-w-[120px]">
                        {slot.bookingDetails.session_type}
                      </p>
                    </div>
                  )}

                  {/* Toggle Button */}
                  <button
                    onClick={() => toggleSlot(slot.time, slot.is_open)}
                    disabled={isProcessing || slot.is_booked}
                    className={`
                                w-full py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all
                                ${isActive
                        ? "bg-white border border-gray-200 text-gray-600 hover:bg-red-50 hover:text-red-600 hover:border-red-200"
                        : "bg-white border border-gray-200 text-green-600 hover:bg-green-50 hover:border-green-200"
                      }
                                disabled:opacity-50 disabled:cursor-not-allowed
                            `}
                  >
                    {isProcessing ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader2 className="w-3 h-3 animate-spin" /> Processing
                      </span>
                    ) : slot.is_booked ? (
                      "Booked"
                    ) : (
                      isActive ? "Block Slot" : "Unblock Slot"
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSlots;